import { useEffect, useState } from 'react'
import { Box, Typography, Divider, Stack, Checkbox, Button, CircularProgress } from '@mui/material'
import SVG from 'react-inlinesvg'
import { SVGS } from '@/assets/svgs'
import { Ego, Note, Topic } from '@/types'
import { NoteType, OddlyouNoteType, PrivacyType, TopicType, TopicTypes } from '@/utils/constants'
import { useMangePinCollections } from '@/hooks/api/dashboard/useManagePinCollections'
import { useAddNoteTopic, useCreateTopic } from '@/hooks/api/dashboard'
import { LoadingButton } from '@mui/lab'
import toast from 'react-hot-toast'
import { getNowInUTC, getTopicFromCreateTopicResponse } from '@/utils/helper'
import { useAuth } from '@/hooks/common'
import { Input } from '@/components'
import colors from '@/styles/colors'

interface SelectionModalProps {
  topic: Topic
  note: Note

  onClose: () => void
}

export const SelectPinCollectionsModal: React.FC<SelectionModalProps> = ({
  topic: sourceTopic,
  note: sourceNote,
  onClose,
}) => {
  const { user } = useAuth()
  const createTopic = useCreateTopic(user?.userID)

  const [selectedIDs, setSelectedIDs] = useState<string[]>([])
  const createTopicNote = useAddNoteTopic()
  const { fetchPins, pinCollections, hasMore, loadMore, isLoading, loadingNextPage } =
    useMangePinCollections(TopicType.PIN_COLLECTION, PrivacyType.ALL)

  const [saving, setIsSaving] = useState(false)

  // --- inline "create collection" state ---
  const [newCollectionName, setNewCollectionName] = useState('')
  const [localNewCollections, setLocalNewCollections] = useState<Topic[]>([])

  useEffect(() => {
    fetchPins(1)
  }, [])

  const toggleItem = (id: string) => {
    setSelectedIDs((prev) =>
      prev.includes(id) ? prev.filter((existing) => existing !== id) : [...prev, id],
    )
  }

  const hasSelection = selectedIDs.length > 0

  const onLoadMore = () => {
    if (hasMore) {
      loadMore()
    }
  }

  const onCreateCollection = async () => {
    const name = newCollectionName.trim()
    if (!name || createTopic.isLoading) return

    try {
      const topicType = TopicType.PIN_COLLECTION
      const result = await createTopic.mutateAsync({
        topicName: name,
        topicType: topicType.toString(),
        allowContribution: 0,
        topicXData: 'X Data Here',
        privacyType: PrivacyType.PRIVATE,
        cliques: [],
        connections: [],
      })

      const newTopic = getTopicFromCreateTopicResponse(result, name, topicType)
      if (newTopic) {
        setLocalNewCollections((prev) => [newTopic, ...prev])
        setSelectedIDs((prev) => [...prev, newTopic.topicID])
        setNewCollectionName('')
      }

      setNewCollectionName('')
    } catch {
      toast.error('Could not create collection')
    } finally {
    }
  }

  const onClickConfirm = async () => {
    toast.loading('Saving Pin...', { id: 'pin_photo' })

    const pinInfo = {
      sourceTopicID: sourceTopic.topicID,
      sourceTopicName: sourceTopic.topicName,
      sourceTopicType: sourceTopic.topicType,
      sourceNoteID: sourceNote.noteID,
      ownerID: sourceNote.authorID,
      ownerProfileName: sourceNote.authProfileName,
      ownerProfileThumbURL: sourceNote.authProfileThumbURL,
      text: '',
    }

    setIsSaving(true)
    for (const topicID of selectedIDs) {
      const response = await createTopicNote.mutateAsync({
        topicID: topicID,
        noteName: 'Pin',
        noteText: JSON.stringify(pinInfo),
        noteType: OddlyouNoteType.PIN.toString(),
        egoItems: sourceNote.egoList,
      })
    }

    toast.remove('pin_photo')
    toast.success('Saved Successfully.', { duration: 2000 })
    setIsSaving(false)
    onClose()
  }

  const displayedCollections = [...localNewCollections, ...pinCollections]

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 10000,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          width: '480px',
          maxHeight: '75%',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden', // prevents the outer box itself from scrolling/growing
        }}
      >
        {/* Fixed header */}
        <Box
          sx={{
            p: '32px',
            pb: 1.5,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <Box>
            <Typography variant='h1' fontWeight={700}>
              {'Pin Collections'}
            </Typography>
            <Typography variant='subtitle1' color='text.secondary'>
              {"Select the collections you'd like to pin this to"}
            </Typography>
          </Box>

          <SVG
            src={SVGS.CrossIcon}
            width={16}
            height={16}
            onClick={onClose}
            style={{ cursor: 'pointer', flexShrink: 0 }}
          />
        </Box>

        <Divider sx={{ flexShrink: 0, mx: '32px' }} />

        {/* Scrollable list */}
        <Box sx={{ overflowY: 'auto', flex: 1, px: '32px', py: 1.5 }}>
          {/* Inline create-collection row */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 1,
              mb: 1,
              borderRadius: '6px',
              border: '1px dashed',
              borderColor: 'divider',
              transition: 'border-color 0.15s ease',
              '&:focus-within': {
                borderColor: 'primary.main',
                borderStyle: 'solid',
              },
            }}
          >
            <Typography
              sx={{
                flexShrink: 0,
                opacity: 0.5,
                fontSize: '1.2rem',
                fontWeight: 500,
                lineHeight: 1,
              }}
            >
              +
            </Typography>

            <Input
              fullWidth
              value={newCollectionName}
              placeholder={'Enter new collection name'}
              inputProps={{ maxLength: 30 }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setNewCollectionName(e.target.value)
              }
              sx={{
                py: 1,
                backgroundColor: 'white',
                borderRadius: '8px',
                color: 'text.primary',
              }}
            />

            <LoadingButton
              size='small'
              variant='text'
              loading={createTopic.isLoading}
              disabled={!newCollectionName.trim()}
              onClick={onCreateCollection}
              sx={{
                minWidth: 'auto',
                px: 1.5,
                borderRadius: '6px',
                fontWeight: 600,
              }}
            >
              Add
            </LoadingButton>
          </Box>

          <Stack spacing={0.5}>
            {pinCollections.length == 0 && isLoading && (
              <Box sx={{ p: 2, alignSelf: 'center' }}>
                <CircularProgress size={24} thickness={3} sx={{ color: '#6366f1' }} />
              </Box>
            )}
            {displayedCollections.map((item) => {
              const isSelected = selectedIDs.includes(item.topicID)

              return (
                <Box
                  key={item.topicID}
                  onClick={() => toggleItem(item.topicID)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 1.5,
                    py: 1,
                    borderRadius: 1.5,
                    cursor: 'pointer',
                    bgcolor: isSelected ? 'action.hover' : 'transparent',
                    transition: 'background-color 0.15s ease',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <Typography variant='body2' fontWeight={500} color='text.primary'>
                    {item.topicName}
                  </Typography>

                  <Checkbox
                    checked={isSelected}
                    onChange={() => toggleItem(item.topicID)}
                    onClick={(e) => e.stopPropagation()}
                    size={'medium'}
                  />
                </Box>
              )
            })}
          </Stack>

          {hasMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              {pinCollections.length > 0 && loadingNextPage ? (
                <CircularProgress size={22} />
              ) : (
                <Button size='small' onClick={onLoadMore}>
                  Load More
                </Button>
              )}
            </Box>
          )}
        </Box>

        {/* Fixed footer */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            px: '32px',
            py: 3,
            flexShrink: 0,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Button variant='outlined' onClick={onClose}>
            Cancel
          </Button>

          <LoadingButton
            variant='contained'
            size='small'
            disabled={!hasSelection}
            onClick={onClickConfirm}
            loading={saving}
          >
            {'Confirm'}
          </LoadingButton>
        </Box>
      </Box>
    </Box>
  )
}
